import { useSelector } from 'react-redux'
import { ILeadOppProduct } from '@user/store/leadOppProducts/typings'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts/slice'
import { serviceTotal } from '@user/components/Services/AddServiceForm/typings'
import { useCallback, useMemo } from 'react'
import { useHandlers } from '@user/components/Services/hooks/useHandlers'
import { useLocalizationEnabled } from '@user/store/tenantFeatures/hooks/useLocalizationEnabled'

type Totals = {
  discounts: number
  first: number
  initial: number
  initialWithTax: number
  recurring: number
  subsequent: number
  tax: number
  monthlyPayment: number
  gstHstTaxes: Record<string, number>
  pstQstTaxes: Record<string, number>
}

export function useData() {
  const products = useSelector(selectAllLeadOppProducts)
  const isCanadianTax = useLocalizationEnabled()
  const { getMonthlyPayment } = useHandlers()
  const label =
    products.length === 1 ? 'Service or Product' : 'Services or Products'

  const getTotalTax = useCallback(
    (service: ILeadOppProduct, taxRate: number, avaoidTax: boolean) => {
      const {
        annualOccurrences,
        chargeTax,
        initialDiscountAmount,
        initialDiscountType,
        initialPrice,
        quantity,
        recurringDiscountAmount,
        recurringDiscountType,
        recurringPrice,
      } = service

      if (!chargeTax || avaoidTax || initialPrice + recurringPrice < 0) return 0

      const total = serviceTotal(
        initialPrice -
          (initialDiscountType === 'dollar'
            ? initialDiscountAmount
            : initialPrice * initialDiscountAmount),
        recurringPrice -
          (recurringDiscountType === 'dollar'
            ? recurringDiscountAmount
            : recurringPrice * recurringDiscountAmount),
        annualOccurrences,
        quantity,
      )

      return total * taxRate
    },
    [],
  )

  const {
    discounts,
    first,
    initial,
    initialWithTax,
    recurring,
    subsequent,
    tax,
    gstHstTaxes,
    pstQstTaxes,
    monthlyPayment,
  } = useMemo(
    () =>
      products.reduce<Totals>(
        (previous, current): Totals => {
          const gstHstTaxAmount = getTotalTax(
            current,
            current.gstHstRate,
            !current.canadianTaxRate,
          )
          const pstQstTaxAmount = getTotalTax(
            current,
            current.pstQstRate,
            !current.canadianTaxRate,
          )

          // Accumulate GST/HST taxes by label
          if (current.gstHstLabel) {
            previous.gstHstTaxes[current.gstHstLabel] =
              (previous.gstHstTaxes[current.gstHstLabel] || 0) + gstHstTaxAmount
          }

          // Accumulate PST/QST taxes by label
          if (current.pstQstLabel) {
            previous.pstQstTaxes[current.pstQstLabel] =
              (previous.pstQstTaxes[current.pstQstLabel] || 0) + pstQstTaxAmount
          }

          const initialSubtotal =
            (current.initialPrice -
              (current.initialDiscountType === 'dollar'
                ? current.initialDiscountAmount
                : current.initialPrice * current.initialDiscountAmount)) *
            current.quantity

          return {
            discounts:
              previous.discounts +
              (current.initialDiscountType === 'dollar'
                ? current.initialDiscountAmount
                : current.initialPrice *
                  current.initialDiscountAmount *
                  current.quantity) +
              (current.recurringDiscountType === 'dollar'
                ? current.recurringDiscountAmount
                : current.recurringPrice *
                  current.recurringDiscountAmount *
                  current.quantity),
            first: previous.first + current.totalFirst,
            initial: previous.initial + initialSubtotal,
            initialWithTax:
              previous.initialWithTax +
              initialSubtotal +
              (current.chargeTax && initialSubtotal > 0
                ? initialSubtotal * current.taxRate
                : 0),
            recurring:
              previous.recurring +
              (current.recurringPrice -
                (current.recurringDiscountType === 'dollar'
                  ? current.recurringDiscountAmount
                  : current.recurringPrice * current.recurringDiscountAmount)) *
                current.quantity,
            subsequent: previous.subsequent + current.totalSubsequent,
            tax:
              previous.tax +
              getTotalTax(
                current,
                current.taxRate,
                isCanadianTax ? current.canadianTaxRate : false,
              ),
            gstHstTaxes: previous.gstHstTaxes,
            pstQstTaxes: previous.pstQstTaxes,
            monthlyPayment:
              previous.monthlyPayment + getMonthlyPayment(current),
          }
        },
        {
          discounts: 0,
          first: 0,
          initial: 0,
          initialWithTax: 0,
          recurring: 0,
          subsequent: 0,
          tax: 0,
          gstHstTaxes: {},
          pstQstTaxes: {},
          monthlyPayment: 0,
        },
      ),
    [products, getTotalTax, isCanadianTax, getMonthlyPayment],
  )

  const hideSubsequent = useMemo(
    () => products.every((p) => p.annualOccurrences === 0),
    [products],
  )

  return {
    discounts,
    first,
    hideSubsequent,
    initial,
    initialWithTax,
    label,
    recurring,
    subsequent,
    tax,
    monthlyPayment,
    gstHstTaxes,
    pstQstTaxes,
  } as const
}
